import { useState, useEffect } from 'react'
import { Box, List, Divider, ListItem, ListItemIcon, ListItemText } from '@mui/material'
import Button from '@app/stories/Button'
import Typography from '@app/stories/Typography'
import MisheLoginBackgroundBuilderDynamic from '@app/components/SvgBuilders/MisheLoginBackgroundBuilderDynamic'
import Icon from '@app/stories/Icons'
import covetHealthLogo from '@app/assets/images/PricingPageLogos/covetHealth.png'
import inovaCareLogo from '@app/assets/images/PricingPageLogos/inovaCare.webp'
import marpaiLogo from '@app/assets/images/PricingPageLogos/marpai.webp'
import mccLogo from '@app/assets/images/PricingPageLogos/mccLogo.png'
import sageLogo from '@app/assets/images/PricingPageLogos/sage.png'
import GetInTouchSection from '@app/components/GetInTouchSection'
import { scrollToHash } from '@app/libs/scroll'
import LogoContainer from '@app/components/LogoContainer'

import { createLightBackgroundColor, lightenHexColor } from '@app/utils/hexcodeUtils'

import { useWhiteLabelingContext } from '@app/context/WhiteLabelingContext'

const PricingPage = () => {
  const { planColorPrimary } = useWhiteLabelingContext()
  const [loginWidth, setLoginWidth] = useState('')
  const [loginHeight, setLoginHeight] = useState('')

  const lightBackgroundColor = createLightBackgroundColor(planColorPrimary)
  const superLightBackgroundColor = lightenHexColor(lightBackgroundColor, 10)

  const BackgroundDynamic = MisheLoginBackgroundBuilderDynamic({
    width: loginWidth,
    height: loginHeight,
    color: superLightBackgroundColor,
  })

  useEffect(() => {
    const updateDimensions = () => {
      const loginBackground = document.getElementById('loginBackground')
      const loginBackgroundMobile = document.getElementById('loginBackgroundMobile')
      if (window.innerWidth >= 900 && loginBackground) {
        const { offsetWidth: width, offsetHeight: height } = loginBackground
        setLoginWidth(width)
        setLoginHeight(height)
      }
      if (window.innerWidth < 900 && loginBackgroundMobile) {
        const { offsetWidth: width, offsetHeight: height } = loginBackgroundMobile
        setLoginWidth(width)
        setLoginHeight(height)
      }
    }

    window.addEventListener('resize', updateDimensions)
    updateDimensions()

    return () => window.removeEventListener('resize', updateDimensions)
  }, [])

  return (
    <Box>
      <Box
        display='flex'
        flexDirection='column'
        justifyContent='flex-start'
        alignItems='center'
        position='relative'
        sx={{
          padding: {
            xs: '24px 16px 16px 16px',
            md: '48px',
          },
        }}
      >
        <Box
          id='loginBackground'
          position='absolute'
          top='0px'
          left='0px'
          width='100%'
          height='100%'
          zIndex='-1'
          overflow='hidden'
          padding='8px'
        >
          {BackgroundDynamic}
        </Box>
        <Box maxWidth='900px'>
          <Box>
            <Typography
              variant='h4'
              align='left'
              weight='light'
              sx={{
                marginBottom: '1rem',
                fontSize: { xs: '2rem', md: '3rem' },
                textShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
              }}
            >
              Working with Mishe | <b>Pricing</b>
            </Typography>
            <Typography
              variant='h2'
              align='left'
              color='primary'
              sx={{
                fontSize: { xs: '2rem', md: '3rem' },
                textShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
              }}
              style={{
                margin: '0',
              }}
            >
              Mishe offers the Direct Payer Platform as a standalone solution and an integrated
              solution with preferred TPA partners.
            </Typography>
          </Box>
          <Box
            display='flex'
            gap='16px'
            height='min-content'
            width='100%'
            my={4}
            sx={{
              flexDirection: {
                xs: 'column',
                md: 'row',
              },
            }}
          >
            <Box
              width='50%'
              height='100%'
              borderRadius='8px'
              bgcolor={superLightBackgroundColor}
              padding='16px'
              boxShadow='0px 4px 8px rgba(0, 0, 0, 0.1)'
              sx={{
                width: {
                  xs: '100%',
                  md: '50%',
                },
                border: `1px solid ${lightBackgroundColor}`,
              }}
            >
              <Typography variant='subtitle2' fontWeight='bold' gutterBottom>
                PAYER PLATFORM
              </Typography>
              <Typography variant='h3' fontWeight='bold' component='div'>
                $20{' '}
                <Typography variant='h6' component='span'>
                  /PEPM
                </Typography>
              </Typography>
              <Typography variant='body2' mb={2}>
                or 10% transaction fee
              </Typography>
              <Divider sx={{ backgroundColor: '#652d92', my: 2 }} />
              <List dense>
                {[
                  'Benefit and navigation portal',
                  'Scheduling & care coordination',
                  'Claims repricing (medical, rx)',
                  'Direct contract negotiations',
                  'Same-day provider payment',
                ].map((text) => (
                  <ListItem key={text} disableGutters>
                    <ListItemIcon sx={{ color: '#652d92', minWidth: 32 }}>
                      <Icon name='checkCircle' size={16} />
                    </ListItemIcon>
                    <ListItemText primary={text} primaryTypographyProps={{ fontSize: 14 }} />
                  </ListItem>
                ))}
              </List>
              <Button
                variant='contained'
                fullWidth
                size='small'
                onClick={() => {
                  if (window.innerWidth < 900) {
                    scrollToHash('#getInTouch', -10)
                  } else {
                    scrollToHash('#getInTouch', 60)
                  }
                }}
              >
                Book a demo
              </Button>
            </Box>
            <Box
              width='50%'
              height='100%'
              borderRadius='8px'
              bgcolor={planColorPrimary}
              padding='16px'
              color='#fff'
              boxShadow='0px 4px 8px rgba(0, 0, 0, 0.1)'
              sx={{
                width: {
                  xs: '100%',
                  md: '50%',
                },
              }}
            >
              <Typography variant='subtitle2' fontWeight='bold' gutterBottom>
                INTEGRATED SOLUTION
              </Typography>
              <Typography variant='h3' fontWeight='bold' component='div'>
                $40{' '}
                <Typography variant='h6' component='span'>
                  /PEPM
                </Typography>
              </Typography>
              <Divider sx={{ backgroundColor: '#fff', my: 2, mt: '52px' }} />
              <List dense>
                {[
                  'All Payer Platform features',
                  'TPA medical admin (inc. UM)',
                  'Full service Life Cycle Mgt',
                  'Compliance (TIC, NSA)',
                  'Add-on carrier network access',
                ].map((text) => (
                  <ListItem key={text} disableGutters>
                    <ListItemIcon sx={{ color: '#fff', minWidth: 32 }}>
                      <Icon name='checkCircle' size={16} />
                    </ListItemIcon>
                    <ListItemText primary={text} primaryTypographyProps={{ fontSize: 14 }} />
                  </ListItem>
                ))}
              </List>
              <Button
                variant='contained'
                color='secondary'
                fullWidth
                size='small'
                sx={{
                  backgroundColor: '#fff',
                  color: planColorPrimary,
                  '&:hover': {
                    backgroundColor: '#fff',
                    color: planColorPrimary,
                  },
                }}
                onClick={() => {
                  if (window.innerWidth < 900) {
                    scrollToHash('#getInTouch', -10)
                  } else {
                    scrollToHash('#getInTouch', 60)
                  }
                }}
              >
                Talk to sales
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        bgcolor={planColorPrimary}
        height='min-content'
        display='flex'
        flexDirection='column'
        padding='48px'
      >
        <Typography
          variant='h1'
          align='center'
          sx={{
            margin: '16px auto',
            fontSize: { xs: '2rem', md: '3rem' },
            textShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
            color: '#fff',
          }}
        >
          For companies of all sizes
        </Typography>
        <Typography
          variant='h5'
          align='center'
          weight='light'
          sx={{
            margin: '0 auto',
            fontSize: { xs: '2rem', md: '3rem' },
            textShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
            color: '#fff',
          }}
        >
          We work with companies of all shapes and sizes — from startups to nationwide enterprises —
          to give their teams great healthcare.
        </Typography>
        <Box
          display='flex'
          justifyContent='center'
          marginTop='20px'
          sx={{
            flexDirection: {
              xs: 'column',
              md: 'row',
            },
            alignItems: {
              xs: 'center',
              md: 'flex-start',
            },
          }}
        >
          <LogoContainer logoSource={mccLogo} altText='mcc' customWidth='60px' />
          <LogoContainer logoSource={covetHealthLogo} altText='covetHealth' />
          <LogoContainer logoSource={inovaCareLogo} altText='inovaCare' />
          <LogoContainer logoSource={marpaiLogo} altText='marpai' />
          <LogoContainer logoSource={sageLogo} altText='sage' customMT={'14px'} />
        </Box>
      </Box>
      <GetInTouchSection />
    </Box>
  )
}

export default PricingPage
